import * as React from "react";
import { cn } from "@/lib/utils";
import { formatNumber, parseLocaleNumber, getDecimalSeparator, getThousandsSeparator } from "@/lib/numberFormat";

export interface NumberInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  /** The numeric value */
  value: number | string | undefined;
  /** Callback when value changes - receives the raw number value */
  onValueChange: (value: number) => void;
  /** Number of decimal places for display (default: 2) */
  decimals?: number;
  /** Allow negative numbers (default: false) */
  allowNegative?: boolean;
  /** Placeholder text */
  placeholder?: string;
}

/**
 * A number input component with locale-aware formatting.
 * Displays numbers with appropriate thousands and decimal separators based on browser locale.
 * Accepts input in either EU (1.234,56) or US (1,234.56) format.
 */
const NumberInput = React.forwardRef<HTMLInputElement, NumberInputProps>(
  ({ className, value, onValueChange, decimals = 2, allowNegative = false, placeholder = "0", ...props }, ref) => {
    const [displayValue, setDisplayValue] = React.useState<string>('');
    const [isFocused, setIsFocused] = React.useState(false);
    
    // Update display value when prop value changes (and not focused)
    React.useEffect(() => {
      if (!isFocused) {
        if (value === undefined || value === null || value === '' || (typeof value === 'number' && isNaN(value))) {
          setDisplayValue('');
        } else {
          const numValue = typeof value === 'string' ? parseFloat(value) : value;
          if (!isNaN(numValue)) {
            setDisplayValue(formatNumber(numValue, decimals));
          }
        }
      }
    }, [value, decimals, isFocused]);
    
    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      // On focus, show raw number for easier editing
      if (value !== undefined && value !== null && value !== '') {
        const numValue = typeof value === 'string' ? parseFloat(value) : value;
        if (!isNaN(numValue)) {
          // Show with decimal separator appropriate for locale
          const decimalSep = getDecimalSeparator();
          setDisplayValue(numValue.toString().replace('.', decimalSep));
        }
      }
      props.onFocus?.(e);
    };
    
    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      // On blur, format the display value
      const parsed = parseLocaleNumber(displayValue);
      if (!isNaN(parsed)) {
        setDisplayValue(formatNumber(parsed, decimals));
        onValueChange(parsed);
      } else if (displayValue.trim() === '') {
        setDisplayValue('');
        onValueChange(0);
      }
      props.onBlur?.(e);
    };
    
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.target.value;
      
      // Allow empty input
      if (input === '') {
        setDisplayValue('');
        onValueChange(0);
        return;
      }
      
      const decimalSep = getDecimalSeparator();
      const thousandsSep = getThousandsSeparator();
      
      // Build a regex that allows: digits, decimal separator, thousands separator, and optionally minus
      const allowedChars = allowNegative 
        ? new RegExp(`^-?[0-9${escapeRegex(decimalSep)}${escapeRegex(thousandsSep)}]*$`)
        : new RegExp(`^[0-9${escapeRegex(decimalSep)}${escapeRegex(thousandsSep)}]*$`);
      
      if (!allowedChars.test(input)) {
        return; // Invalid input, don't update
      }
      
      setDisplayValue(input);
      
      // Try to parse and update the value
      const parsed = parseLocaleNumber(input);
      if (!isNaN(parsed)) {
        onValueChange(parsed);
      }
    };
    
    return (
      <input
        type="text"
        inputMode="decimal"
        className={cn(
          "flex h-10 w-full rounded-md border border-input bg-card px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
          className
        )}
        ref={ref}
        value={displayValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        {...props}
      />
    );
  }
);

NumberInput.displayName = "NumberInput";

// Helper to escape special regex characters
function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export { NumberInput };
